import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Divider,
  IconButton
} from '@mui/material';
import { ChromePicker } from 'react-color';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../../services/api';

const BrandingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [brandingSettings, setBrandingSettings] = useState({
    companyName: 'Hakiki Project Management',
    companyLogo: null,
    theme: {
      primaryColor: '#1976D2',
      secondaryColor: '#424242',
      accentColor: '#82B1FF'
    }
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState({
    primary: false,
    secondary: false,
    accent: false
  });

  // Menggunakan useCallback untuk memastikan fungsi fetchBrandingSettings stabil antar render
  const fetchBrandingSettings = useCallback(async () => {
    setLoading(true);
    try {
      // In a real app, you would fetch branding settings from the API
      // For now, we'll use mock data
      const response = await api.get('/admin/settings').catch(() => {
        // If API endpoint doesn't exist yet, use default settings
        return { data: { settings: brandingSettings } };
      });
      
      setBrandingSettings(response.data.settings);
      if (response.data.settings.companyLogo) {
        setLogoPreview(response.data.settings.companyLogo);
      }
    } catch (error) {
      console.error('Error fetching branding settings:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat pengaturan branding',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [brandingSettings, setAlert]);
  
  useEffect(() => {
    fetchBrandingSettings();
  }, [fetchBrandingSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBrandingSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleColorChange = (color, type) => {
    setBrandingSettings(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [type]: color.hex
      }
    }));
  };

  const handleToggleColorPicker = (type) => {
    setShowColorPicker(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleLogoChange = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setBrandingSettings(prev => ({
      ...prev,
      companyLogo: null
    }));
  };

  const handleSaveBrandingSettings = async () => {
    setSaving(true);
    
    try {
      // If there's a new logo file, upload it first
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        
        const logoResponse = await api.post('/admin/settings/logo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Update settings with new logo URL
        setBrandingSettings(prev => ({
          ...prev,
          companyLogo: logoResponse.data.logoUrl
        }));
      }
      
      // Save theme settings
      await api.put('/admin/settings/theme', {
        primaryColor: brandingSettings.theme.primaryColor,
        secondaryColor: brandingSettings.theme.secondaryColor,
        accentColor: brandingSettings.theme.accentColor
      });
      
      // Save company name (this endpoint might not exist yet)
      await api.put('/admin/settings', {
        companyName: brandingSettings.companyName
      }).catch(() => {
        // If API endpoint doesn't exist yet, just ignore
      });
      
      setAlert({
        open: true,
        message: 'Pengaturan branding berhasil disimpan',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving branding settings:', error);
      setAlert({
        open: true,
        message: 'Gagal menyimpan pengaturan branding',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Branding & Umum
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Sesuaikan tampilan aplikasi dengan branding perusahaan Anda
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Logo Perusahaan
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload logo perusahaan untuk ditampilkan di header dan halaman login
            </Typography>
            
            <Card sx={{ mb: 3, maxWidth: 400 }}>
              {logoPreview ? (
                <>
                  <CardMedia
                    component="img"
                    image={logoPreview}
                    alt="Company Logo Preview"
                    sx={{ height: 200, objectFit: 'contain', bgcolor: '#f5f5f5' }}
                  />
                  <CardContent sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                    <IconButton size="small" onClick={handleRemoveLogo} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </CardContent>
                </>
              ) : (
                <CardContent sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Belum ada logo yang diupload
                  </Typography>
                </CardContent>
              )}
            </Card>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload Logo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleLogoChange}
              />
            </Button>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Nama Perusahaan
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Nama perusahaan yang akan ditampilkan di aplikasi
            </Typography>
            
            <TextField
              fullWidth
              label="Nama Perusahaan"
              name="companyName"
              value={brandingSettings.companyName}
              onChange={handleInputChange}
              margin="normal"
            />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Warna Tema / UI
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Sesuaikan warna tema aplikasi dengan branding perusahaan Anda
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Warna Utama (Primary)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: brandingSettings.theme.primaryColor,
                    mr: 2,
                    cursor: 'pointer',
                    border: '1px solid #ddd'
                  }}
                  onClick={() => handleToggleColorPicker('primary')}
                />
                <TextField
                  size="small"
                  value={brandingSettings.theme.primaryColor}
                  InputProps={{ readOnly: true }}
                  onClick={() => handleToggleColorPicker('primary')}
                  sx={{ width: 120 }}
                />
              </Box>
              {showColorPicker.primary && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <ChromePicker
                    color={brandingSettings.theme.primaryColor}
                    onChange={(color) => handleColorChange(color, 'primaryColor')}
                    disableAlpha
                  />
                </Box>
              )}
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Warna Sekunder (Secondary)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: brandingSettings.theme.secondaryColor,
                    mr: 2,
                    cursor: 'pointer',
                    border: '1px solid #ddd'
                  }}
                  onClick={() => handleToggleColorPicker('secondary')}
                />
                <TextField
                  size="small"
                  value={brandingSettings.theme.secondaryColor}
                  InputProps={{ readOnly: true }}
                  onClick={() => handleToggleColorPicker('secondary')}
                  sx={{ width: 120 }}
                />
              </Box>
              {showColorPicker.secondary && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <ChromePicker
                    color={brandingSettings.theme.secondaryColor}
                    onChange={(color) => handleColorChange(color, 'secondaryColor')}
                    disableAlpha
                  />
                </Box>
              )}
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Warna Aksen (Accent)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: brandingSettings.theme.accentColor,
                    mr: 2,
                    cursor: 'pointer',
                    border: '1px solid #ddd'
                  }}
                  onClick={() => handleToggleColorPicker('accent')}
                />
                <TextField
                  size="small"
                  value={brandingSettings.theme.accentColor}
                  InputProps={{ readOnly: true }}
                  onClick={() => handleToggleColorPicker('accent')}
                  sx={{ width: 120 }}
                />
              </Box>
              {showColorPicker.accent && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <ChromePicker
                    color={brandingSettings.theme.accentColor}
                    onChange={(color) => handleColorChange(color, 'accentColor')}
                    disableAlpha
                  />
                </Box>
              )}
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  sx={{ bgcolor: brandingSettings.theme.primaryColor }}
                >
                  Primary Button
                </Button>
                <Button
                  variant="contained"
                  sx={{ bgcolor: brandingSettings.theme.secondaryColor }}
                >
                  Secondary Button
                </Button>
                <Button
                  variant="contained"
                  sx={{ bgcolor: brandingSettings.theme.accentColor }}
                >
                  Accent Button
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSaveBrandingSettings}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Simpan Pengaturan'}
        </Button>
      </Box>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandingSettings;
